import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Trophy, Share2, Users, X, MessageCircle, Send } from "lucide-react";
import { trackEvent } from "@/lib/audit";
import { openShareSheet } from "@/components/viral/ShareSheetHost";

/**
 * Hosts the post-prediction Reward → Share → Referral moments as a single
 * non-invasive bottom card. Listens to first_* events emitted by the
 * activation pipeline. Each moment auto-dismisses or yields to the next
 * when the user takes action — never opens ShareSheet automatically.
 */
type Phase = "reward" | "share" | "referral" | null;

const SEEN_KEYS = {
  reward: "vs:conv_moment_reward_seen",
  share: "vs:conv_moment_share_seen",
  referral: "vs:conv_moment_referral_seen",
};

function seen(k: string): boolean {
  if (typeof window === "undefined") return true;
  try { return !!window.localStorage.getItem(k); } catch { return false; }
}
function markSeen(k: string) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(k, String(Date.now())); } catch { /* noop */ }
}

export function ConversionMomentsHost() {
  const [phase, setPhase] = useState<Phase>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onEvent = (e: Event) => {
      const detail = (e as CustomEvent<{ event_type: string }>).detail;
      if (!detail) return;
      if (detail.event_type === "first_prediction_completed" && !seen(SEEN_KEYS.reward)) {
        setPhase("reward");
        markSeen(SEEN_KEYS.reward);
        void trackEvent("conversion_reward_moment_shown", {});
        window.setTimeout(() => {
          setPhase((p) => (p === "reward" ? "share" : p));
          if (!seen(SEEN_KEYS.share)) {
            markSeen(SEEN_KEYS.share);
            void trackEvent("conversion_share_moment_shown", {});
          }
        }, 3500);
      } else if (detail.event_type === "first_share_completed" && !seen(SEEN_KEYS.referral)) {
        setPhase("referral");
        markSeen(SEEN_KEYS.referral);
        void trackEvent("conversion_referral_moment_shown", {});
      }
    };
    window.addEventListener("vs:event", onEvent as EventListener);
    return () => window.removeEventListener("vs:event", onEvent as EventListener);
  }, []);

  if (!phase || typeof document === "undefined") return null;

  const close = () => setPhase(null);

  const onShareClick = (target: "whatsapp" | "telegram" | "system") => {
    void trackEvent("conversion_share_moment_clicked", { target });
    try { openShareSheet(); } catch { /* noop */ }
    close();
  };

  const onReferralClick = () => {
    void trackEvent("conversion_referral_moment_clicked", {});
    try { openShareSheet(); } catch { /* noop */ }
    close();
  };

  const node = (
    <div
      role="dialog"
      aria-label="Conversion moment"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex justify-center px-3 pb-[calc(96px+env(safe-area-inset-bottom))] sm:pb-6"
    >
      <div className="pointer-events-auto w-full max-w-md overflow-hidden rounded-2xl border border-primary/40 bg-background/95 p-3 shadow-2xl backdrop-blur animate-in slide-in-from-bottom-4 fade-in duration-300">
        <button
          onClick={close}
          aria-label="Cerrar"
          className="absolute right-2 top-2 rounded-full p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        {phase === "reward" && (
          <div className="flex items-start gap-3 pr-6">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Trophy className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-primary">🎉 Primera predicción</div>
              <div className="text-sm font-black">🏆 +50 puntos para tu nación</div>
              <p className="mt-1 text-[11px] text-muted-foreground">🌎 Tu nación te necesita — sigue jugando.</p>
            </div>
          </div>
        )}

        {phase === "share" && (
          <div>
            <div className="flex items-start gap-3 pr-6">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Share2 className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-black uppercase tracking-widest text-primary">🔥 Comparte tu predicción</div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Cada amigo que se registre suma puntos para tu nación.
                </p>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <button onClick={() => onShareClick("whatsapp")} className="rounded-lg bg-[oklch(0.7_0.15_145)] px-2 py-2 text-[11px] font-black text-background flex items-center justify-center gap-1">
                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
              </button>
              <button onClick={() => onShareClick("telegram")} className="rounded-lg bg-[oklch(0.7_0.15_230)] px-2 py-2 text-[11px] font-black text-background flex items-center justify-center gap-1">
                <Send className="h-3.5 w-3.5" /> Telegram
              </button>
              <button onClick={() => onShareClick("system")} className="rounded-lg bg-primary px-2 py-2 text-[11px] font-black text-primary-foreground flex items-center justify-center gap-1">
                <Share2 className="h-3.5 w-3.5" /> Compartir
              </button>
            </div>
          </div>
        )}

        {phase === "referral" && (
          <div>
            <div className="flex items-start gap-3 pr-6">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Users className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-black uppercase tracking-widest text-primary">⚽ Invita a un amigo</div>
                <ul className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
                  <li>+50 puntos por invitado</li>
                  <li>+250 puntos al siguiente nivel</li>
                  <li>+500 puntos nivel oro</li>
                </ul>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary">
                  <div className="h-full w-[10%] bg-gradient-to-r from-[oklch(0.58_0.24_27)] to-[oklch(0.82_0.24_145)]" />
                </div>
              </div>
            </div>
            <button
              onClick={onReferralClick}
              className="mt-3 w-full rounded-lg bg-primary px-3 py-2 text-[12px] font-black text-primary-foreground"
            >
              Invitar a un amigo ahora
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}